import { useEffect } from "react";
import type { ImageStyle, StyleProp } from "react-native";
import Animated, {
  Easing,
  useAnimatedStyle,
  useSharedValue,
  withDelay,
  withTiming,
} from "react-native-reanimated";

type Props = {
  /** require()'d image source for the 3D hero icon. */
  source: number;
  /** Width in pixels. Defaults to 80. */
  width?: number;
  /** Height in pixels. Defaults to 96. */
  height?: number;
  style?: StyleProp<ImageStyle>;
  /** Test handle (surfaces as data-testid on web); the image has no accessible name. */
  testID?: string;
};

export function DialogIcon({
  source,
  width = 80,
  height = 96,
  style,
  testID,
}: Props) {
  const rotate = useSharedValue(-8);
  const opacity = useSharedValue(0);

  useEffect(() => {
    rotate.value = withDelay(
      180,
      withTiming(0, { duration: 720, easing: Easing.out(Easing.cubic) }),
    );
    opacity.value = withDelay(
      180,
      withTiming(1, { duration: 360, easing: Easing.out(Easing.ease) }),
    );
  }, [rotate, opacity]);

  const animatedStyle = useAnimatedStyle(() => ({
    transform: [{ rotate: `${rotate.value}deg` }],
    opacity: opacity.value,
  }));

  return (
    <Animated.Image
      source={source}
      testID={testID}
      style={[{ width, height, alignSelf: "center" }, animatedStyle, style]}
      resizeMode="contain"
    />
  );
}
